import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "overview-page" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "line-separator" }, null, -1)
const _hoisted_3 = { class: "overview-header" }
const _hoisted_4 = { class: "overview-text" }
const _hoisted_5 = { class: "overview-tiles" }
const _hoisted_6 = { class: "overview-subscription-table" }

import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AWSTile from '@/components/Cloud/Dashboard/Components/aws-tiles.vue';
import AWSHeader from '@/components/Cloud/Dashboard/Components/aws-overview-header.vue';
import AWSTable from '@/components/Cloud/Dashboard/Components/aws-table.vue';
import AWSAlertTable from '@/components/Cloud/Dashboard/Components/aws-alert-table.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import {
  alertTableToggleOptions,
  alertsMapper,
  lambdaOverviewTableHeader,
} from '@/components/Cloud/Dashboard/cloud.types';
import {
  elastiCacheOverviewHeader,
  MIN_SLICE,
  MAX_SLICE,
  SUBSCRIPTION_MAX_SLICE,
  SUBTYPE,
  OVERVIEW_PAGE,
  SUBSCRIPTION_PAGE,
} from '@/components/Cloud/Dashboard/pages/elasticache/elasticache.constants';
import { useStoreData } from '@/components/Common/useStoreData';
import { useStoreDataCloud } from '@/components/Cloud/Dashboard/useStoreData';
import { useGetServicesForTeamByDuration } from '@/components/Cloud/Hooks/useGetServicesForTeamByDuration';
import { useGetTopAlertsPerService } from '@/components/Cloud/Hooks/useGetTopAlertsPerService';
import {
  Subscriptions,
  TilesData,
} from '@/components/Cloud/Hooks/cloud.all.types';
import { useTopAlertsByMetrics } from '@/components/Cloud/Hooks/useTopAlertsByMetricsData';
import { useMatricesData } from '@/components/Cloud/Hooks/useMetricsData';
import { convertBytes } from '@/utils/helper-function';
// Setup i18n

export default /*#__PURE__*/_defineComponent({
  __name: 'overview-page',
  setup(__props) {

const { t } = useI18n();

const route = useRoute() || {};
const router = useRouter();
const { getDurationTime } = useStoreData();
const {
  setCurrentPage,
  setCurrentService,
  setCurrentServices,
  getCurrentServices,
} = useStoreDataCloud();

// Reactive state
const ingressOverviewHeader = computed(() => [
  ...lambdaOverviewTableHeader(),
  ...elastiCacheOverviewHeader(),
]);
const subscriptions = ref<Subscriptions[]>([]);
const tilesData = ref<TilesData[]>([]);
const isLoading = ref(false);

// Computed properties
const getAlertMatricNameMapper = computed(() => alertsMapper);
const getCurrentSubType = computed(() => SUBTYPE);

const { data: services, fetchServicesForTeamByDuration } =
  useGetServicesForTeamByDuration({
    subType: getCurrentSubType.value,
  });
const { data: topAlertPerService, fetchTopAlertsPerService } =
  useGetTopAlertsPerService({
    subType: getCurrentSubType.value,
  });
const { subscriptionMatrices, filteredSubscriptionMatrices, fetchMatrices } =
  useMatricesData({
    headers: ingressOverviewHeader,
    subscriptions,
  });

const { alertData, fetchTopAlerts } = useTopAlertsByMetrics(
  subscriptions,
  getAlertMatricNameMapper,
);

const getServiceLevelTopAlert = computed(() => {
  return topAlertPerService.value?.map((data) => ({
    subscriptionId: data.serviceName,
    alerts: data.count,
  }));
});

// Handlers
const handleSubscriptionSelect = (selectedSubscriptions) => {
  filteredSubscriptionMatrices.value =
    selectedSubscriptions.length > 0
      ? subscriptionMatrices.value
          .filter((matrices) =>
            selectedSubscriptions.includes(matrices.subscriptionId),
          )
          .slice(MIN_SLICE, MAX_SLICE)
      : [];

  setCurrentServices(
    subscriptions.value
      .filter((item) => selectedSubscriptions.includes(item.label))
      .map((item) => item.key)
      .slice(MIN_SLICE, MAX_SLICE),
  );
};

const handleSubscriptionClick = (subscriptionId) => {
  const service = route.params.service;
  const serviceId = subscriptions.value.find(
    (service) => service.label === subscriptionId,
  )?.key;
  setCurrentService(serviceId);
  router.push({
    name: SUBSCRIPTION_PAGE,
    params: { subscriptionId, serviceId, service },
  });
};

const getInitialData = (params) => {
  const { isInitialRender } = params ?? {};
  try {
    isLoading.value = true;

    subscriptions.value = services.value?.map((service) => ({
      ...service,
      selected: false,
    }));
    setCurrentServices(
      !isInitialRender
        ? subscriptions.value
            .filter((subscription) =>
              getCurrentServices.value.includes(subscription.key),
            )
            .map((item) => item.key)
        : getCurrentServices.value.length
          ? subscriptions.value
              .filter((subscription) =>
                getCurrentServices.value.includes(subscription.key),
              )
              .map((item) => item.key)
          : subscriptions.value
              .map((item) => item.key)
              .slice(MIN_SLICE, SUBSCRIPTION_MAX_SLICE),
    );

    fetchMatrices();
    fetchTopAlerts();
  } catch (error) {
    console.error('Error in beforeMount:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleFilterSubscriptionMatrices = (currentValue) => {
  try {
    tilesData.value = [
      ...elastiCacheOverviewHeader().map((header) => {
        const key = header.key.toLowerCase();
        const isAverage = header.value === 'avg';
        let content = currentValue.reduce((accumulator, currValue) => {
          accumulator += parseInt(currValue[key]);
          return accumulator;
        }, 0);
        const average = content / currentValue.length;
        content = isAverage ? (isNaN(average) ? 0 : average) : content;

        let finalContent =
          typeof content === 'object'
            ? content[key]?.toFixed?.(isAverage ? 2 : 0)
            : content?.toFixed?.(isAverage ? 2 : 0);

        // Check if header.convertTo exists and use convertBytes
        // if (header.convertTo) {
        //   finalContent = convertBytes(finalContent, header.convertTo);
        // }

        return {
          title: header.tileName,
          content: finalContent,
          tooltip: header.tooltip,
        };
      }),
    ];
  } catch (error) {
    console.error('Error in filteredSubscriptionMatrices watcher:', error);
  }
};

// Watchers
watch(filteredSubscriptionMatrices, handleFilterSubscriptionMatrices);
watch(getDurationTime, () => {
  fetchServicesForTeamByDuration();
  fetchTopAlertsPerService();
  fetchTopAlerts();
});
watch(services, () => {
  getInitialData({
    isInitialRender: true,
  });
});

// Lifecycle hooks
onMounted(() => {
  setCurrentPage(OVERVIEW_PAGE);
  setCurrentServices([]);

  fetchServicesForTeamByDuration();
  fetchTopAlertsPerService();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createVNode(RexSpinV3, { spinning: isLoading.value }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(AWSHeader, {
            onHandleSubscriptionSelect: handleSubscriptionSelect,
            subscriptions: subscriptions.value,
            isShowSelectDb: false
          }, null, 8, ["subscriptions"])
        ]),
        _createElementVNode("h4", _hoisted_4, _toDisplayString(_unref(t)('aws.overviewHeading')), 1),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(AWSTile, {
            tiles: tilesData.value,
            numberOfRows: "2"
          }, null, 8, ["tiles"]),
          _createVNode(AWSAlertTable, {
            toggleOptions: _unref(alertTableToggleOptions)(),
            alertsData: getServiceLevelTopAlert.value,
            totalAlertsData: _unref(alertData),
            alertType: "subscription"
          }, null, 8, ["toggleOptions", "alertsData", "totalAlertsData"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(AWSTable, {
            placeholder: _unref(t)('aws.searchSubscriptionId'),
            title: _unref(t)('aws.subscriptionOverview'),
            headers: ingressOverviewHeader.value,
            data: _unref(filteredSubscriptionMatrices),
            onSubscriptionClicked: handleSubscriptionClick,
            isToggleEnabled: true,
            currentSubType: _unref(SUBTYPE)
          }, null, 8, ["placeholder", "title", "headers", "data", "currentSubType"])
        ])
      ]),
      _: 1
    }, 8, ["spinning"])
  ]))
}
}

})